import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { optimizeImageUrl } from "../utils/images";

const VALUES = [
  { title: "Authenticity", text: "Every bottle we carry is 100% genuine, sourced directly from authorized distributors." },
  { title: "Elegance", text: "From curation to packaging, every detail is thoughtfully crafted for the discerning customer." },
  { title: "Individuality", text: "We believe fragrance is personal. Our collection celebrates your unique identity." },
  { title: "Confidence", text: "The right scent is the final touch of any outfit, and we help you wear yours with pride." },
];

const TEAM = [
  { name: "Aderinsola Adebiyi", role: "Founder & CEO", img: "https://res.cloudinary.com/dirgb6nie/image/upload/v1777313328/WhatsApp_Image_2026-04-27_at_7.08.11_PM_ybvoix.jpg" },
];

const FALLBACK = optimizeImageUrl("https://images.unsplash.com/photo-1590736969955-71cc94901144?w=200&q=80", { width: 500 });
const HERO_IMAGE = optimizeImageUrl(
  "https://res.cloudinary.com/dirgb6nie/image/upload/v1775254817/e42d39e0-5f7a-4201-bad7-bc1e7e5b1054_yd1ycl.jpg",
  { width: 1600 }
);

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <div className="relative bg-brand-dark pt-36 pb-28 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url('${HERO_IMAGE}')` }} />
        <div className="relative max-w-7xl mx-auto">
          <span className="section-label text-brand-red">Our Story</span>
          <h1 className="font-display text-5xl sm:text-7xl text-white font-light mt-3 leading-tight max-w-xl">Born from a Love of Scent</h1>
        </div>
      </div>

      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="section-label">About Tiwani</span>
            <h2 className="section-title mt-3 mb-6">Curating Timeless<br />Luxury, Just for You</h2>
            <p className="font-body text-gray-400 leading-relaxed mb-5">
              Tiwani is a modern fragrance brand dedicated to crafting elegant, long-lasting perfumes that capture style, mood, and essence. Designed for young adults who appreciate minimalism and authenticity, Tiwani perfumes embody sophistication while remaining accessible to those who value quality and individuality.
            </p>
            <p className="font-body text-gray-400 leading-relaxed mb-8">
              The brand blends elegance, confidence, and personal expression into every scent, creating fragrances that not only last but also make a statement. With a clean and minimal aesthetic, Tiwani appeals to individuals who seek timeless fragrances that reflect their unique identity and lifestyle.
            </p>
            <Link to="/products" className="btn-primary group">
              Explore Collection <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="relative w-full max-w-2xl lg:ml-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:h-[500px] lg:items-stretch">
              <div className="rounded-sm overflow-hidden bg-brand-cream h-[300px] sm:h-[360px] lg:h-full min-h-0">
                <img
                  src={optimizeImageUrl("https://res.cloudinary.com/dirgb6nie/image/upload/v1776723912/WhatsApp_Image_2026-04-20_at_11.13.23_PM_bm8eg8.jpg", { width: 900 })}
                  alt="Luxury perfume"
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  onError={(e) => { e.currentTarget.src = FALLBACK; }}
                />
              </div>
              <div className="grid gap-4 h-auto lg:h-full lg:grid-rows-[minmax(0,1fr)_120px] min-h-0">
                <div className="rounded-sm overflow-hidden bg-brand-cream h-[220px] sm:h-[260px] lg:h-full min-h-0">
                  <img
                    src={optimizeImageUrl("https://res.cloudinary.com/dirgb6nie/image/upload/v1776723898/WhatsApp_Image_2026-04-20_at_11.13.23_PM_1_ndsp7l.jpg", { width: 700 })}
                    alt="Tiwani collection"
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                    sizes="(min-width: 1024px) 24vw, 100vw"
                    onError={(e) => { e.currentTarget.src = FALLBACK; }}
                  />
                </div>
                <div className="rounded-sm bg-brand-red min-h-[120px] p-6 flex items-center justify-center">
                  <div className="text-center">
                    <p className="font-body text-[10px] text-white/70 uppercase tracking-[0.3em]">100%</p>
                    <p className="font-display text-2xl text-white font-semibold mt-2">Authentic Products</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-cream px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="section-label">What Drives Us</span>
            <h2 className="section-title mt-2">Our Core Values</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map(({ title, text }) => (
              <div key={title} className="bg-white p-8 rounded-sm border border-gray-100 hover:border-brand-red hover:shadow-lg transition-all duration-300 group">
                <div className="w-8 h-1 bg-brand-red mb-5 group-hover:w-14 transition-all duration-300" />
                <h3 className="font-display text-2xl text-brand-dark mb-3">{title}</h3>
                <p className="font-body text-sm text-gray-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="section-label">The People</span>
            <h2 className="section-title mt-2">Meet Our Team</h2>
          </div>
          <div className="grid gap-8 max-w-sm mx-auto">
            {TEAM.map(({ name, role, img }) => (
              <div key={name} className="text-center group">
                <div className="relative w-44 h-44 mx-auto mb-5 rounded-sm overflow-hidden">
                  <img src={optimizeImageUrl(img, { width: 500 })} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="eager" fetchPriority="high" sizes="176px" onError={(e) => { e.currentTarget.src = FALLBACK; }} />
                  <div className="absolute inset-0 bg-brand-red/0 group-hover:bg-brand-red/10 transition-colors duration-300" />
                </div>
                <h3 className="font-display text-xl text-brand-dark">{name}</h3>
                <p className="font-body text-[11px] uppercase tracking-widest text-brand-red mt-1">{role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
